import { customSendNotifyBody } from "../../functions/shared/notify-helpers/customSendNotifyBody";
import { IOrder, User } from "../../store/slices/userSlice";
import { ACCESS_KEY, urlAPI } from "../../urls";
import { addDaysISO } from "./landlordEvents/helpers/dateHelpers";

const sleep = (ms: number) => new Promise(res => setTimeout(res, ms));

export type SendResult = 'ok' | 'skip' | 'error';

async function sendOneSafe(
  recipient: RecipientPreview,
  title: string,
  body: string,
  route: string
): Promise<SendResult> {
  if (!hasRealPushToken(recipient.deviceToken)) return 'skip';
  try {
    await customSendNotifyBody({
      id: recipient.id,
      device: recipient.deviceToken!,
      title,
      text: body,
      url: route,
      silent: false
    });
    return 'ok';
  } catch {
    for (let attempt = 1; attempt <= 2; attempt++) {
      await new Promise(r => setTimeout(r, attempt * 1000));
      try {
        await customSendNotifyBody({
          id: recipient.id,
          device: recipient.deviceToken!,
          title,
          text: body,
          url: route,
          badges: 0,
          silent: false
        });
        return 'ok';
      } catch { /* retry */ }
    }
    return 'error';
  }
}
export async function sendQueue1Rps(
  recipients: RecipientPreview[],
  title: string,
  body: string,
  route: string | ((recipient: RecipientPreview) => string),
  onProgress?: (i: number, total: number, r: SendResult) => void,
  isCancelled?: () => boolean
): Promise<{ ok: number; skip: number; error: number }> {
  let ok = 0, skip = 0, error = 0;
  const total = recipients.length;

  for (let i = 0; i < total; i++) {
    if (isCancelled?.()) break;
    const recipient = recipients[i];
    const res = await sendOneSafe(recipient, title, body, typeof route === 'function' ? route(recipient) : route);
    if (res === 'ok') ok++; else if (res === 'skip') skip++; else error++;
    onProgress?.(i + 1, total, res);
    if (i < total - 1) await sleep(1000); // 1 rps
  }
  return { ok, skip, error };
}

export const hasRealPushToken = (token: string | null | undefined): boolean => {
  if (!token) return false;
  return !/browser\s*token/i.test(token);
};

export interface RecipientPreview {
  id: number
  fullName: string
  email: string
  phone: string | null
  deviceToken: string | null
  category: string
  isVerified: boolean
  hasUnits: boolean
  searchableName: string
  username: string
  blocked?: boolean
  blockedReason?: string | null
}

export const toRecipientPreview = (user: User): RecipientPreview => {
  const firstName = user.ContactInfo?.FirstName ?? ''
  const familyName = user.ContactInfo?.FamilyName ?? ''
  const fullName = [firstName, familyName].filter(Boolean).join(' ').trim() || user.username || user.email || `User #${user.id}`
  return { id: user.id, fullName, email: user.email ?? user.ContactInfo?.Email ?? '', phone: user.ContactInfo?.Phone ?? user.phone ?? null, deviceToken: user.DeviceToken ?? null, category: typeof user.Category === 'string' ? user.Category : 'Unknown', isVerified: false, hasUnits: Array.isArray(user.Units) && user.Units.length > 0, searchableName: fullName.toLowerCase(), username: user.username ?? '', blocked: user.blocked === true, blockedReason: user.blockedReason ?? null }
}

export const selectInsiderPushRecipients = (users: User[], audience: string): RecipientPreview[] => {
  const allowed = audience === 'Agent' ? new Set(['Agent']) : audience === 'Landlord' ? new Set(['Landlord', 'Client']) : new Set(['Agent', 'Landlord', 'Client'])
  const unique = new Map<string, RecipientPreview>()
  users.map(toRecipientPreview).filter((recipient) => allowed.has(recipient.category) && hasRealPushToken(recipient.deviceToken)).forEach((recipient) => unique.set(recipient.deviceToken ?? `user:${recipient.id}`, recipient))
  return Array.from(unique.values())
}

export interface EventHistoryEntry {
  id: string
  title: string
  description: string
  recipientsLabel: string
  deepLink: string | null
  createdAt: string
  scheduledFor: string | null
  status: HistoryStatus
  statusMessage?: string
  completedAt?: string
}

export interface FiltersState {
  id: string
  name: string
  email: string
  category: string
  verified: VerifiedFilter
  blocked: BlockedFilter
  hasUnitsOnly: boolean
  pushableOnly: boolean;
}

export type DeliveryMode = 'immediate' | 'scheduled'
export type ExecuteAtMode = 'picker' | 'raw'
export type SubmitStatus = 'idle' | 'sending' | 'success' | 'error'
export type HistoryStatus = 'pending' | 'sent' | 'failed'
export type VerifiedFilter = 'all' | 'verified' | 'unverified'
export type BlockedFilter = 'all' | 'blocked' | 'unblocked'

const jwt = window.localStorage.getItem(ACCESS_KEY) ?? ''


export async function grantRewardTrialForUser(
  userId: number,
  days: number
): Promise<boolean> {
  try {
    const validUntil = addDaysISO(days)

    await api({
      method: 'PUT',
      collection: 'users',
      id: Number(userId),
      body: {
        RewardTrialValidDate: validUntil,
      },
    })

    return true
  } catch (e) {
    console.error('[grantRewardTrialForUser] error', e)
    return false
  }
}

export async function blockUserWithReason(
  userId: number,
  blockedReason: string
): Promise<{ ok: boolean; user?: any; error?: string }> {
  try {
    const reason = blockedReason.trim()
    if (!reason) {
      return { ok: false, error: 'Blocking reason is required.' }
    }

    const updatedUser = await api({
      method: 'PUT',
      collection: 'users',
      id: Number(userId),
      body: {
        blocked: true,
        blockedReason: reason,
      },
    })

    return { ok: true, user: updatedUser }
  } catch (e: any) {
    return { ok: false, error: e?.message ?? 'Failed to block user.' }
  }
}

export async function unblockUser(
  userId: number
): Promise<{ ok: boolean; user?: any; error?: string }> {
  try {
    const updatedUser = await api({
      method: 'PUT',
      collection: 'users',
      id: Number(userId),
      body: {
        blocked: false,
        blockedReason: null,
      },
    })

    return { ok: true, user: updatedUser }
  } catch (e: any) {
    return { ok: false, error: e?.message ?? 'Failed to unblock user.' }
  }
}


export async function grantRewardTrialBatch(
  recipients: RecipientPreview[],
  days: number,
  onProgress?: (done: number, total: number) => void
): Promise<{ ok: number; error: number }> {
  let ok = 0
  let error = 0
  let done = 0

  for (const r of recipients) {
    try {
      const success = await grantRewardTrialForUser(r.id, days)
      success ? ok++ : error++
    } catch {
      error++
    } finally {
      done++
      onProgress?.(done, recipients.length)
    }
  }

  return { ok, error }
}











export async function grantTrialPremium(userId: number, days: number, user: any): Promise<boolean> {
  try {
    console.log(user,"user")

    const { start, end } = trialDates(days);

    const orders = user.Orders ?? [];
    console.log(orders,"orders")
    const hasFull = orders.every((o: any) => o && typeof (o as any).Service !== 'undefined');

    const isPaid = (o?: IOrder | null) => !!o?.Statuses?.some(s => String(s).toLowerCase() === 'paid');
    const isBroker = (o?: IOrder | null) => !!o?.Service?.some(s =>
      s.__component === 'services.broker-subscription' || /broker/i.test(s.Package)
    );

    let target: IOrder | null = null;

    if (hasFull) {
      target = [...orders]
        .filter(o => isPaid(o) && isBroker(o))
        .sort((a, b) => ((b.updatedAt || b.createdAt || '') > (a.updatedAt || a.createdAt || '') ? 1 : -1))[0] ?? null;
    } else {
      // в Orders могут быть "пустышки"/id — дотянем ровно столько, сколько нужно
      const ids = orders.map((o: any) => Number(o?.id ?? o)).filter((n: any) => Number.isFinite(n));
      for (const id of ids) {
        const full = await getOrderById(id); // тянем с populate Service
        console.log(full,"full <<< full")
        if (isPaid(full) && isBroker(full)) { target = full; break; }
      }
    }
   console.log(target,"target <<<<<< target")
    if (target) {

      // 2) Обновляем существующий ордер (только даты триала в нужном сервисе)
      const nextServices = target.Service.map(s =>
        (s.__component === 'services.broker-subscription' || /broker/i.test(s.Package))
          ? { ...s, TrialStartDate: start, TrialEndDate: end } // доп. поля — ок
          : s
      );

      await api({
        method: 'PUT',
        collection: 'orders',
        id: Number(target.id),
        body: { Statuses: target.Statuses ?? ['Paid'], Service: nextServices }
      });

      // запишем транзакцию
      await appendTransaction(userId, user);
      return true;
    }


    if (!target) {
      console.warn('[grantTrialPremium] no active Paid broker subscription for user', userId);
      return false;
    }

    // 3) Активного нет — создаём новый "Paid" broker-ордер
    // const service: any = {
    //   __component: 'services.broker-subscription',
    //   Package: 'Premium',
    //   StartDate: start,
    //   ExpiryDate: end,
    // };

    // const created = await api({
    //   method: 'POST',
    //   collection: 'orders',
    //   body: {
    //     Name: `${userId}_${user?.Category ?? 'User'}_PremiumTrial_${Date.now()}`,
    //     Statuses: ['Paid'],
    //     Service: [service],
    //     User: Number(userId),
    //     TrialStartDate: start,
    //     TrialEndDate: end,
    //   }
    // });

   // const newId = Number(created?.data?.id ?? created?.id);
  //  const prevIds = (user.Orders ?? []).map((o: any) => Number(o?.id ?? o)).filter(Number.isFinite);
   // const unique = Array.from(new Set([...prevIds, newId]));

    // await api({
    //   method: 'PUT',
    //   collection: 'users',
    //   id: Number(userId),
    //   body: { Orders: unique }
    // });

    await appendTransaction(userId, user);
    return true;
  } catch (e) {
    console.error('[grantTrialPremium] error:', e);
    return false;
  }
}



const trialDates = (days: number) => {
  const toYMD = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const now = new Date();
  const end = new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
  return { start: toYMD(now), end: toYMD(end) };
};

async function api(payload: any) {
  const res = await fetch(urlAPI, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'BSO-Hash': '02ns30nsBSO!',
      Authorization: `Bearer ${jwt}`
    },
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (!json?.success || json?.response?.error) {
    throw new Error(JSON.stringify(json?.response?.error || json));
  }
  return json.response;
}

async function getOrderById(id: number): Promise<IOrder> {
  const r = await api({ method: 'GET', collection: 'orders', id, query: { populate: ['Service'] } });
  return r?.data ?? r;
}

async function appendTransaction(userId: number, user: User) {
  const entry = {
    product: 'Premium Trial Broker Subscription',
    timestamp: Date.now(),
    amount: 0,
    amountType: 'AED',
    action: 'purchase',
  };
  const prev = (user as any).TransactionsHistory ?? [];
  await api({
    method: 'PUT',
    collection: 'users',
    id: Number(userId),
    body: { TransactionsHistory: [...prev, entry] }
  });
}

export async function grantTrialBatch(
  recipients: RecipientPreview[],
  days: number,
  onProgress?: (done: number, total: number) => void
): Promise<{ ok: number; error: number }> {
  let ok = 0, error = 0, done = 0;
  for (const r of recipients) {
    try {
      (await grantTrialPremium(r.id, days, r)) ? ok++ : error++;
    } catch {
      error++;
    } finally {
      done++;
      onProgress?.(done, recipients.length);
    }
  }
  return { ok, error };
}
